import { useMemo, useState } from 'react';

import { ApiError } from '../../lib/api-client';
import { getSession } from '../../lib/auth';
import { CkEditorField } from '../../components/ui/CkEditorField';
import { confirmBillUpdate } from '../../lib/confirm-service';
import { useToast } from '../../lib/toast';
import { FieldLabel, LookupSelect } from './investigation-bill.shared';
import { useInvestigationBillLookup, useUpdateInvestigationBill } from './investigation-bill.queries';
import { currency, formatInvestigationDateTime, todayInput } from './investigation-bill.utils';
import type { InvestigationBill, InvestigationPayment, UpdateInvestigationBillPayload } from './types';

interface DraftCharge { charge: string; name: string; rate: number; quantity: number; discountPercent: number; discountAmount: number }

const lineAmount = (line: Pick<DraftCharge, 'rate' | 'quantity' | 'discountPercent' | 'discountAmount'>) => {
  const gross = line.rate * line.quantity;
  return Math.max(0, gross - Math.max(line.discountAmount, gross * line.discountPercent / 100));
};

const toDateTimeInput = (value: string) => {
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

export const InvestigationBillEditBillForm = ({ row, onSaved }: { row: InvestigationBill; onSaved?: () => void }) => {
  const { showToast } = useToast();
  const mutation = useUpdateInvestigationBill(row.id);
  const doctors = useInvestigationBillLookup('doctors');
  const referrals = useInvestigationBillLookup('referrals');
  const providers = useInvestigationBillLookup('providers');
  const marketByOptions = useInvestigationBillLookup('market-by');
  const charges = useInvestigationBillLookup('charges');

  const [billDate, setBillDate] = useState(() => toDateTimeInput(row.billDate));
  const [doctor, setDoctor] = useState(row.referringDoctor?.id ?? '');
  const [referredBy, setReferredBy] = useState(row.referredBy?.id ?? '');
  const [provider, setProvider] = useState(row.provider?.id ?? '');
  const [marketBy, setMarketBy] = useState(row.marketBy?.id ?? '');

  const [lines, setLines] = useState<DraftCharge[]>(() => row.charges.map((line) => ({ charge: line.charge, name: line.chargeName, rate: line.rate, quantity: line.quantity, discountPercent: line.discountPercent, discountAmount: line.discountAmount })));
  const [chargeId, setChargeId] = useState('');
  const [draftRate, setDraftRate] = useState(0);
  const [draftQuantity, setDraftQuantity] = useState(1);
  const [draftDiscountPercent, setDraftDiscountPercent] = useState(0);
  const [draftDiscountAmount, setDraftDiscountAmount] = useState(0);

  const [payments, setPayments] = useState<InvestigationPayment[]>([{ mode: 'Cash', bankName: null, amount: 0 }, { mode: 'UPI', bankName: 'Axis Bank', amount: 0 }]);
  const [discountType, setDiscountType] = useState(row.billing.discountType);
  const [totalDiscount, setTotalDiscount] = useState(row.billing.totalDiscount);
  const [financeRemarks, setFinanceRemarks] = useState(row.financeRemarks ?? '');
  const [partyRemarks, setPartyRemarks] = useState(row.partyRemarks ?? '');

  const total = useMemo(() => lines.reduce((sum, line) => sum + lineAmount(line), 0), [lines]);
  const discountAmount = discountType === 'percentage' ? total * totalDiscount / 100 : totalDiscount;
  const grandTotal = Math.max(0, total - discountAmount);
  const newPayment = payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const totalPay = row.billing.totalPayment + newPayment;
  const totalDue = Math.max(0, grandTotal - totalPay);

  const resetDraft = () => { setChargeId(''); setDraftRate(0); setDraftQuantity(1); setDraftDiscountPercent(0); setDraftDiscountAmount(0); };
  const chooseCharge = (id: string) => {
    setChargeId(id);
    const selected = charges.data?.find((item) => item.id === id);
    setDraftRate(selected?.rate ?? 0);
    setDraftQuantity(1);
    setDraftDiscountPercent(0);
    setDraftDiscountAmount(0);
  };
  const draftAmount = lineAmount({ rate: draftRate, quantity: draftQuantity, discountPercent: draftDiscountPercent, discountAmount: draftDiscountAmount });
  const addCharge = () => {
    const selected = charges.data?.find((item) => item.id === chargeId);
    if (!selected || lines.some((line) => line.charge === selected.id)) return;
    setLines((current) => [...current, { charge: selected.id, name: selected.label, rate: draftRate, quantity: draftQuantity, discountPercent: draftDiscountPercent, discountAmount: draftDiscountAmount }]);
    resetDraft();
  };
  const updateLine = (index: number, patch: Partial<DraftCharge>) => setLines((current) => current.map((line, lineIndex) => lineIndex === index ? { ...line, ...patch } : line));
  const updatePayment = (index: number, patch: Partial<InvestigationPayment>) => setPayments((current) => current.map((payment, paymentIndex) => paymentIndex === index ? { ...payment, ...patch } : payment));

  const editorName = getSession()?.user.name ?? 'Staff User';
  const editingDate = todayInput().split('-').reverse().join('-');

  const canUpdate = lines.length > 0;
  const updateBill = async () => {
    if (!canUpdate) return;
    if (!(await confirmBillUpdate())) return;
    const payload: UpdateInvestigationBillPayload = {
      billDate,
      referringDoctor: doctor || null,
      referredBy: referredBy || null,
      provider: provider || null,
      marketBy: marketBy || null,
      charges: lines.map((line) => ({ charge: line.charge, rate: line.rate, quantity: line.quantity, discountPercent: line.discountPercent, discountAmount: line.discountAmount })),
      payments: payments.filter((payment) => payment.amount > 0),
      discountType,
      totalDiscount,
      financeRemarks: financeRemarks.trim() || null,
      partyRemarks: partyRemarks.trim() || null,
    };
    try {
      await mutation.mutateAsync(payload);
      showToast('Investigation bill updated successfully.', 'success');
      onSaved?.();
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : 'Investigation bill could not be updated.', 'error');
    }
  };

  return (
    <div className="investigation-bill-prescription">
      <header className="investigation-bill-detail-banner">
        <div className="investigation-bill-detail-banner-row">
          <div className="investigation-bill-detail-banner-fields">
            <span>Bill No : {row.billing.billNumber}</span>
            <span>Bill Date : {formatInvestigationDateTime(row.billDate)}</span>
            <span>Bill Section : Investigation</span>
          </div>
        </div>
        <p>Bill Created By - {row.generatedBy.name}</p>
      </header>

      <section className="investigation-bill-section">
        <div className="investigation-bill-edit-bill-fields">
          <label><FieldLabel required>Bill Date</FieldLabel><input type="datetime-local" value={billDate} onChange={(event) => setBillDate(event.target.value)} /></label>
          <label><FieldLabel required>Referring Doctor</FieldLabel><LookupSelect id="investigation-bill-edit-bill-doctor" value={doctor} options={doctors.data} placeholder="Select Doctor" clearable={false} onChange={setDoctor} /></label>
          <label><FieldLabel>Referred By</FieldLabel><LookupSelect id="investigation-bill-edit-bill-referred-by" value={referredBy} options={referrals.data} placeholder="Select" onChange={setReferredBy} /></label>
          <label><FieldLabel>Provider</FieldLabel><LookupSelect id="investigation-bill-edit-bill-provider" value={provider} options={providers.data} placeholder="Select" onChange={setProvider} /></label>
          <label><FieldLabel>Market By</FieldLabel><LookupSelect id="investigation-bill-edit-bill-market-by" value={marketBy} options={marketByOptions.data} placeholder="Select" onChange={setMarketBy} /></label>
        </div>
      </section>

      <section className="investigation-bill-section"><h2>Charges</h2>
        <div className="table-scroll">
          <table className="investigation-bill-charge-table">
            <thead><tr><th>Date</th><th>Charge Name<span className="investigation-bill-required">*</span></th><th>Rate (₹)<span className="investigation-bill-required">*</span></th><th className="investigation-bill-charge-narrow">#/Cancel</th><th>Qty<span className="investigation-bill-required">*</span></th><th>Dis (%)<span className="investigation-bill-required">*</span></th><th>Dis Amount (₹)<span className="investigation-bill-required">*</span></th><th>Amount (₹)<span className="investigation-bill-required">*</span></th></tr></thead>
            <tbody>
              <tr className="investigation-bill-draft-row">
                <td>{formatInvestigationDateTime(row.billDate)}</td>
                <td><LookupSelect id="investigation-bill-edit-bill-charge" value={chargeId} options={charges.data} placeholder="Select" onChange={chooseCharge} /></td>
                <td><input type="number" min={0} step="0.01" value={draftRate} onChange={(event) => setDraftRate(Number(event.target.value))} /></td>
                <td className="investigation-bill-charge-narrow"><button className="investigation-bill-add" type="button" onClick={addCharge} disabled={!chargeId}>+</button></td>
                <td><input type="number" min={1} value={draftQuantity} onChange={(event) => setDraftQuantity(Number(event.target.value))} /></td>
                <td><input type="number" min={0} max={100} step="0.01" value={draftDiscountPercent} onChange={(event) => setDraftDiscountPercent(Number(event.target.value))} /></td>
                <td><input type="number" min={0} step="0.01" value={draftDiscountAmount} onChange={(event) => setDraftDiscountAmount(Number(event.target.value))} /></td>
                <td><strong>{currency(draftAmount)}</strong></td>
              </tr>
              {lines.map((line, index) => (
                <tr key={index}>
                  <td>{formatInvestigationDateTime(row.billDate)}</td>
                  <td>{line.name}</td>
                  <td><input type="number" min={0} step="0.01" value={line.rate} onChange={(event) => updateLine(index, { rate: Number(event.target.value) })} /></td>
                  <td className="investigation-bill-charge-narrow"><button className="investigation-bill-cancel" type="button" onClick={() => setLines((current) => current.filter((_, lineIndex) => lineIndex !== index))} aria-label={`Cancel ${line.name}`}>×</button></td>
                  <td><input type="number" min={1} value={line.quantity} onChange={(event) => updateLine(index, { quantity: Number(event.target.value) })} /></td>
                  <td><input type="number" min={0} max={100} step="0.01" value={line.discountPercent} onChange={(event) => updateLine(index, { discountPercent: Number(event.target.value) })} /></td>
                  <td><input type="number" min={0} step="0.01" value={line.discountAmount} onChange={(event) => updateLine(index, { discountAmount: Number(event.target.value) })} /></td>
                  <td><strong>{currency(lineAmount(line))}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="investigation-bill-section investigation-bill-payment-section"><h2>Payment</h2>
        <div className="investigation-bill-payment-row">
          <div className="investigation-bill-payment-methods">
            <div className="investigation-bill-payment-grid">
              <div style={{ width: '125%' }}><label>Payment Mode<select value="Cash" disabled><option>Cash</option></select></label><label>Payment Amount<input type="number" min={0} step="0.01" value={payments[0]?.amount ?? 0} onChange={(event) => updatePayment(0, { amount: Number(event.target.value) })} /></label></div>
            </div>
            <div className="investigation-bill-payment-grid">
              <div><label>Mode<select value={payments[1]?.mode ?? 'UPI'} onChange={(event) => updatePayment(1, { mode: event.target.value as InvestigationPayment['mode'] })}><option>UPI</option><option>Transfer to Bank Account</option><option>Cheque</option><option>Other</option><option>Online</option></select></label><label>Bank<select value={payments[1]?.bankName ?? ''} onChange={(event) => updatePayment(1, { bankName: event.target.value })}><option>Axis Bank</option><option>Canara Bank</option><option>State Bank</option></select></label><label>Amount<input type="number" min={0} step="0.01" value={payments[1]?.amount ?? 0} onChange={(event) => updatePayment(1, { amount: Number(event.target.value) })} /></label></div>
            </div>
          </div>
          <div className="investigation-bill-total-panel">
            <div><span>Total</span><strong>{currency(total)}</strong></div>
            <div><span>Discount</span><input type="number" min={0} step="0.01" value={totalDiscount} onChange={(event) => setTotalDiscount(Number(event.target.value))} /><select value={discountType} onChange={(event) => setDiscountType(event.target.value as 'flat' | 'percentage')}><option value="flat">Rs.</option><option value="percentage">%</option></select></div>
            <div><span>Grand Total</span><strong>{currency(grandTotal)}</strong></div>
          </div>
        </div>
      </section>

      <section className="investigation-bill-detail-subsection">
        <div className="investigation-bill-detail-subsection-head"><h3>Receipt List</h3><span>Total Payment <strong>{currency(row.billing.totalPayment)}</strong></span></div>
        {row.payments.length === 0 ? (
          <p className="investigation-bill-detail-empty-note">No payments recorded for this bill.</p>
        ) : (
          <div className="table-scroll">
            <table className="investigation-bill-detail-payment-table">
              <thead><tr><th>#</th><th>Date</th><th>Mode</th><th>Bank</th><th>Received By</th><th>Amount</th></tr></thead>
              <tbody>
                {row.payments.map((payment, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{formatInvestigationDateTime(row.billDate)}</td>
                    <td>{payment.mode}</td>
                    <td>{payment.bankName ?? '-'}</td>
                    <td>{row.generatedBy.name}</td>
                    <td>{currency(payment.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="investigation-bill-edit-bill-footer-row">
        <div className="investigation-bill-edit-bill-remarks">
          <label><CkEditorField label="Finance Remarks" value={financeRemarks} onChange={setFinanceRemarks} /></label>
          <label><CkEditorField label="Party/Department Remarks" value={partyRemarks} onChange={setPartyRemarks} /></label>
        </div>
        <div className="investigation-bill-edit-bill-pay-summary">
          <div className="pay"><span>Total Pay (₹)</span><strong>{currency(totalPay)}</strong></div>
          <div className="due"><span>Total Due (₹)</span><strong>{currency(totalDue)}</strong></div>
        </div>
      </div>

      <div className="investigation-bill-form-actions">
        <button className="button button-secondary investigation-bill-danger-outline" type="button" disabled title="Cancelling an Investigation bill isn't available yet">Full Bill Cancel</button>
        <button className="button button-primary" type="button" disabled={!canUpdate || mutation.isPending} onClick={() => void updateBill()}>{mutation.isPending ? 'Updating…' : 'Update Bill'}</button>
        <button className="button button-secondary" type="button" disabled title="Approving an Investigation bill isn't available yet">Bill Save &amp; Approve</button>
      </div>
      <p className="investigation-bill-edit-bill-footer">Editing : {editorName} || Date : {editingDate}</p>
    </div>
  );
};
